/**
 * Reading a price observation off a payload, and the geometry derived from it.
 *
 * Every observation on the wire — the strike, the settlement price, an index
 * tick — is `{ value, raw, expo }` **flat**, since register §F18a reconciled
 * `asyncapi.IndexData` with `openapi.IndexPrice`. Before that the index nested
 * them under `price` on one transport and not the other, which is why this file
 * reads defensively through one function instead of destructuring at eight call
 * sites.
 *
 * **`expo` comes from the payload.** It is 8 *or* 18 decimals per stream and
 * unverified (`docs/05`), the lint bans every spelling of the constant, and the
 * mock deliberately serves the two assets at different scales so a client that
 * assumed one would misprice the other by ten orders of magnitude on its first
 * frame.
 *
 * **`toNumber` is for pixels, never for money.** The tie rule is
 * `comparePrices`/`outcomeOf` on `raw` at equal `expo` and nothing here weakens
 * that. What this file computes is where to put a marker and how to label an
 * axis, and a float is the correct type for a pixel.
 *
 * No React, no DOM: `domain/` is testable without either.
 */

import { comparePrices, type Outcome, type RawPrice } from '@/lib/decimal'

export interface Observation extends RawPrice {
  /** The display decimal, exactly as sent. Never parsed for arithmetic. */
  value: string
}

/**
 * Pull an observation out of a payload, or `null`.
 *
 * `null` is a first-class answer: a `SCHEDULED` market's strike genuinely is
 * NULL, and rendering `—` is the honest response. A zero would be a price.
 */
export function observationFrom(source: unknown): Observation | null {
  if (typeof source !== 'object' || source === null) return null
  const record = source as Record<string, unknown>
  const { value, raw, expo } = record
  if (typeof raw !== 'string' || typeof expo !== 'number') return null
  return { value: typeof value === 'string' ? value : raw, raw, expo }
}

/**
 * The observation as a float, for geometry and display only.
 *
 * At `expo = -18` the mantissa is 22 digits and well past `Number.MAX_SAFE_INTEGER`,
 * so this divides in `BigInt` down to a scale a double holds exactly before
 * converting — `Number(raw) * 10 ** expo` would lose the low digits *and* the
 * high ones.
 */
export function toNumber(observation: Observation | null): number | null {
  if (!observation) return null
  try {
    const expo = observation.expo
    if (expo > 0 || !Number.isInteger(expo)) return null
    const raw = BigInt(observation.raw)
    // Keep six decimal places of a value that may be twenty digits wide: divide
    // the mantissa down in integers, then convert once.
    const dropped = Math.max(0, -expo - 6)
    const scaled = raw / 10n ** BigInt(dropped)
    return Number(scaled) / 10 ** (-expo - dropped)
  } catch {
    return null
  }
}

/** `(a − b) / b` in basis points, for the meter and the tooltip. */
export function bpsBetween(a: number | null, b: number | null): number | null {
  if (a === null || b === null || b === 0) return null
  return ((a - b) / b) * 10_000
}

/**
 * The tie rule, `UP` iff `settle >= strike` (D5).
 *
 * Re-exported through this file so a component reaching for "which way is this
 * going" finds the exact integer comparison rather than writing `>=` on two
 * floats it got from `toNumber`.
 */
export function impliedOutcome(index: Observation | null, strike: Observation | null): Outcome | null {
  if (!index || !strike) return null
  return comparePrices(index, strike) >= 0 ? 'UP' : 'DOWN'
}

// ---------------------------------------------------------------------------
// <StrikeMeter> geometry
// ---------------------------------------------------------------------------

/**
 * The ±5 basis point floor, and it does real work (components.md §2.2).
 *
 * The meter auto-ranges to ±(3 × the trailing 60-second realised range) — one
 * market period, not five. At a 60-second horizon the typical excursion is a
 * handful of basis points, so without a floor a quiet minute renders the marker
 * slamming the edges and the meter reads as pure noise.
 */
export const METER_FLOOR_BPS = 5
export const METER_RANGE_MULTIPLE = 3

export function meterHalfRangeBps(trailingRangeBps: number | null): number {
  if (trailingRangeBps === null || !Number.isFinite(trailingRangeBps)) return METER_FLOOR_BPS
  return Math.max(METER_FLOOR_BPS, Math.abs(trailingRangeBps) * METER_RANGE_MULTIPLE)
}

/**
 * Where the marker sits, as a fraction of the meter's width.
 *
 * `0` is the left edge, `0.5` the centre tick (the strike), `1` the right edge.
 * Clamped, because a marker drawn outside its track is a marker the user cannot
 * see — and the clamp is visible, since the fill reaches the edge.
 */
export function meterPosition(distanceBps: number | null, halfRangeBps: number): number {
  if (distanceBps === null || halfRangeBps <= 0) return 0.5
  return Math.max(0, Math.min(1, 0.5 + distanceBps / (2 * halfRangeBps)))
}

// ---------------------------------------------------------------------------
// <MarketChart> vertical scale
// ---------------------------------------------------------------------------

/**
 * The least the chart's pane may span around the strike: ±0.1 basis point.
 *
 * **The chart fits the bars it is showing, plus the strike, and nothing else
 * decides its height.** This constant is only the guard against a pane of zero
 * height — a feed that printed one price — and it is small enough (±$0.81 on
 * an $81,000 BTC) that it never competes with a real minute.
 *
 * Two earlier rules did compete, and both drew a real feed as a flat line.
 * A fixed ±5 bp, borrowed from `<StrikeMeter>`, is ±$41 on BTC. Then the
 * previous market's high-to-low, on the argument that "this minute will move
 * about as much as the last" — which is false exactly when it matters: a $24
 * minute followed by a $1 minute drew the $1 minute in a $24 pane. **Any floor
 * that is not taken from the bars on screen will flatten a quiet minute**, and
 * on a live BTC feed most minutes are quiet. Both were tuned against
 * `oracle.Fake`'s wave, which moves dollars a second where Chainlink moves
 * cents.
 *
 * What the floor was protecting against — a scale that twitches — mostly does
 * not happen in the `market` timeframe: the visible window is pinned to
 * `[anchor, expiry]`, bars only accumulate inside it, so the fitted range only
 * ever *grows* within a market and resets once, at the roll.
 */
export const CHART_FLOOR_BPS = 0.1

/**
 * A rolling window of index samples, for the trailing realised range.
 *
 * Sixty seconds at the index's 5 Hz is 300 samples — small enough to scan on
 * every tick and short enough to follow a real move. It is a plain ring rather
 * than a store because it is per-component state that no other panel reads.
 */
export class TrailingRange {
  readonly #windowMs: number
  readonly #samples: { atMs: number; value: number }[] = []

  constructor(windowMs = 60_000) {
    this.#windowMs = windowMs
  }

  push(atMs: number, value: number): void {
    this.#samples.push({ atMs, value })
    const cutoff = atMs - this.#windowMs
    while (this.#samples.length > 0 && (this.#samples[0] as { atMs: number }).atMs < cutoff) {
      this.#samples.shift()
    }
  }

  get size(): number {
    return this.#samples.length
  }

  /** High minus low over the window, in basis points of `reference`. `null`
   *  until there are two samples — one sample has no range. */
  rangeBps(reference: number | null): number | null {
    if (this.#samples.length < 2 || reference === null || reference === 0) return null
    let low = Infinity
    let high = -Infinity
    for (const sample of this.#samples) {
      if (sample.value < low) low = sample.value
      if (sample.value > high) high = sample.value
    }
    return ((high - low) / reference) * 10_000
  }

  clear(): void {
    this.#samples.length = 0
  }
}
